/**
 * AddUserModal: pre-register a person so they get Guardian access on their
 * first sign-in. A Carbon Modal (layer-02, pinned header, the footer button
 * set): the Entra object ID, the home tenant, email, display name, role and
 * the tenants they may see.
 *
 * Opened from the organization drawer's "Invite admin" action. The Modal is
 * a headless UI Dialog that portals itself, so it renders above the drawer
 * and a click inside it never reaches the drawer underneath.
 */
import React, { useEffect, useId, useState } from 'react'
import toast from 'react-hot-toast'

import { api } from '@/lib/api'
import type { Tenant } from '@/types'
import { Modal } from '@/components/carbon/Modal'
import { Checkbox } from '@/components/carbon/Checkbox'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { Select } from '@/components/carbon/Select'
import { TextInput } from '@/components/carbon/TextInput'

interface AddUserModalProps {
  isOpen: boolean
  onClose: () => void
  onUserAdded?: () => void
}

interface NewUserData {
  objectId: string
  homeTenantId: string
  email: string
  displayName: string
  role: string
  tenantIds: string[]
}

const ROLES = [
  { value: 'Viewer', label: 'Viewer', description: 'Reads dashboards and changes nothing.' },
  { value: 'Approver', label: 'Approver', description: 'Accepts or rejects staged drift, plus everything a Viewer can do.' },
  { value: 'ComplianceOfficer', label: 'Compliance Officer', description: 'Reads compliance reports and exports data.' },
  { value: 'SecurityAdmin', label: 'Security Admin', description: 'Manages policies and reads all data.' },
  { value: 'GlobalAdmin', label: 'Global Admin', description: 'Full access, including tenant management and user provisioning.' }
]

const EMPTY_USER: NewUserData = {
  objectId: '',
  homeTenantId: '',
  email: '',
  displayName: '',
  role: 'Viewer',
  tenantIds: []
}

export default function AddUserModal({ isOpen, onClose, onUserAdded }: AddUserModalProps) {
  const formId = useId()
  const [userData, setUserData] = useState<NewUserData>(EMPTY_USER)
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  // Load available tenants when modal opens
  useEffect(() => {
    if (isOpen) {
      loadTenants()
      // Reset form
      setUserData(EMPTY_USER)
      setError(null)
      setValidationErrors({})
    }
  }, [isOpen])

  const loadTenants = async () => {
    try {
      const tenants = await api.getTenants()
      setAvailableTenants(tenants)
    } catch (err) {
      console.error('Failed to load tenants:', err)
      toast.error('Tenants did not load. Close and reopen this dialog.')
    }
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    // Validate ObjectId (GUID format)
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (!userData.objectId.trim()) {
      errors.objectId = 'Entra object ID is required.'
    } else if (!guidRegex.test(userData.objectId.trim())) {
      errors.objectId = 'Enter a GUID in the form xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx.'
    }

    // Validate HomeTenantId: required by the API (P0-2 composite identity key).
    // Must be one of the tenants Guardian already knows about so the row lands
    // with a known (HomeTenantId, ObjectId) pair instead of a free-form GUID.
    if (!userData.homeTenantId.trim()) {
      errors.homeTenantId = 'Home tenant is required.'
    } else if (!guidRegex.test(userData.homeTenantId.trim())) {
      errors.homeTenantId = 'Enter a tenant ID in GUID form.'
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!userData.email.trim()) {
      errors.email = 'Email is required.'
    } else if (!emailRegex.test(userData.email.trim())) {
      errors.email = 'Enter a valid email address.'
    }

    // Role is required (dropdown always has a value)
    if (!userData.role) {
      errors.role = 'Role is required.'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)
      setError(null)

      const submitData = {
        objectId: userData.objectId.trim(),
        homeTenantId: userData.homeTenantId.trim(),
        email: userData.email.trim(),
        displayName: userData.displayName.trim() || userData.email.trim(), // Use email as fallback
        role: userData.role,
        tenantIds: userData.tenantIds.length > 0 ? userData.tenantIds : undefined
      }

      await api.preRegisterUser(submitData)

      toast.success(`${userData.email} added. They get access on first sign-in.`)
      onUserAdded?.()
      onClose()
    } catch (err: unknown) {
      console.error('Failed to pre-register user:', err)
      const e = err as { response?: { data?: unknown }; message?: string }
      const errorMessage = e?.response?.data || e?.message || 'Adding the user failed. Check the object ID and try again.'
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage))
      toast.error('Adding the user failed. Check the object ID and try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleTenantToggle = (tenantId: string) => {
    setUserData(prev => ({
      ...prev,
      tenantIds: prev.tenantIds.includes(tenantId)
        ? prev.tenantIds.filter(id => id !== tenantId)
        : [...prev.tenantIds, tenantId]
    }))
  }

  const role = ROLES.find(r => r.value === userData.role)
  const tenantsLoaded = availableTenants.length > 0

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="md"
      title="Add user"
      subtitle="They get Guardian access the first time they sign in."
      secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: loading }}
      primaryAction={{ label: 'Add user', type: 'submit', form: formId, loading }}
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
        {error && (
          <InlineNotification kind="error" title="Adding the user failed.">
            {error}
          </InlineNotification>
        )}

        <InlineNotification kind="info" title="Find the Entra object ID.">
          In the Azure portal, open Microsoft Entra ID, then Users, select the user and copy the Object ID.
        </InlineNotification>

        <TextInput
          id="objectId"
          label="Entra object ID"
          value={userData.objectId}
          onChange={(e) => setUserData(prev => ({ ...prev, objectId: e.target.value }))}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          error={validationErrors.objectId}
          disabled={loading}
          required
          className="font-mono"
        />

        {/* Home Tenant: second half of the composite identity key. The
            user signs in from this Entra tenant; if the same ObjectId
            appears in two different tenants (common for MSP guests),
            HomeTenantId is what keeps them distinct in the DB. */}
        <Select
          id="homeTenantId"
          label="Home tenant"
          value={userData.homeTenantId}
          onChange={(e) => setUserData(prev => ({ ...prev, homeTenantId: e.target.value }))}
          error={validationErrors.homeTenantId}
          helper="Where they sign in. Not always the tenants they watch over."
          disabled={loading || !tenantsLoaded}
          required
        >
          <option value="">
            {tenantsLoaded ? 'Select where this user signs in' : 'Loading tenants'}
          </option>
          {availableTenants.map(tenant => (
            <option key={tenant.tenantId} value={tenant.tenantId}>
              {tenant.name || tenant.tenantId}
              {tenant.domain ? ` (${tenant.domain})` : ''}
            </option>
          ))}
        </Select>

        <TextInput
          id="email"
          type="email"
          label="Email"
          value={userData.email}
          onChange={(e) => setUserData(prev => ({ ...prev, email: e.target.value }))}
          placeholder="user@company.com"
          error={validationErrors.email}
          disabled={loading}
          required
        />

        <TextInput
          id="displayName"
          label="Display name (optional)"
          value={userData.displayName}
          onChange={(e) => setUserData(prev => ({ ...prev, displayName: e.target.value }))}
          placeholder="Jane Doe"
          helper="Guardian shows the email when this is blank."
          disabled={loading}
        />

        <Select
          id="role"
          label="Role"
          value={userData.role}
          onChange={(e) => setUserData(prev => ({ ...prev, role: e.target.value }))}
          options={ROLES.map(r => ({ value: r.value, label: r.label }))}
          helper={role?.description}
          error={validationErrors.role}
          disabled={loading}
        />

        <fieldset className="m-0 min-w-0 border-0 p-0">
          <legend className="mb-2 p-0 text-helper text-text-secondary">Tenant access (optional)</legend>
          <div className="max-h-48 overflow-y-auto border-b border-line-strong bg-field px-4 py-3">
            {!tenantsLoaded ? (
              <p className="m-0 text-body text-text-helper">Loading tenants</p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-3 p-0">
                {availableTenants.map(tenant => (
                  <li key={tenant.tenantId}>
                    <Checkbox
                      checked={userData.tenantIds.includes(tenant.tenantId)}
                      onChange={() => handleTenantToggle(tenant.tenantId)}
                      disabled={loading}
                      label={
                        <>
                          {tenant.name || tenant.tenantId}
                          {tenant.domain && <span className="ml-2 text-text-helper">({tenant.domain})</span>}
                        </>
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className="m-0 mt-1 text-helper text-text-helper">
            The tenants they can see. You can assign more later from the People tab.
          </p>
        </fieldset>
      </form>
    </Modal>
  )
}
